import { createSelector } from "reselect"
import { get, sumBy, chain, forEach, filter, map, find, compact, includes } from "lodash"
import { getFactorShortLabel } from "helpers/factors"
import { getFavoriteFactors } from "modules/portfolio-root/selectors/portfolios"
import { getDecompositionType, getSelectedContentSet } from "./decomposition-types"
import { COLORS } from "helpers/analyzeConstants"
import { makeCustomFactorFormatter, numberFormatter } from "helpers/formatters/numbers"
import { CONTENT_SET_DATA_TYPES, EXPOSURE_CONSTRAINT_WEIGHTINGS } from "helpers/contentSetConstants"
import { getSortedContentSets } from "selectors/contentSets"
import { getModelCategories, getModelFactors } from "selectors/modelMetadata"

const getOptimizationData = state => get(state, "global.data.scorecardData.data.model.optimization")
const getPortfolioData = (state, props) => get(props, "portfolioData")
const getFactorConstraints = state => get(state, "modules.optimization.constraints.exposure")

export const getFactorsCategories = createSelector(
  [getModelCategories, getSelectedContentSet],
  (categories, contentSet) => {
    if (contentSet) {
      return map(contentSet.categories, ({ id, name }, index) => ({
        id, label: name, color: COLORS[index],
      }))
    }
    return categories
  },
)

export const getFactors = createSelector(
  [getModelFactors, getSelectedContentSet],
  (factors, contentSets, contentSetId) => {
    if (contentSetId) {
      const contentSet = find(contentSets, ({ id }) => id === contentSetId)
      if (contentSet) {
        return chain(contentSet.factors)
          .keyBy("id")
          .mapValues(({ name, category }) => ({
            category, label: name,
          }))
          .value()
      }
      return null
    }

    return factors
  },
)

function getCategoryFactors(factors, categoryId) {
  return filter(factors, factor => factor.category === categoryId)
}

function combineFactors(factorsMetadata, rebalancedFactors, originalFactors, favoriteFactors, formatter) {
  const factors = {}

  forEach(rebalancedFactors, ({ id, value, name }) => {
    factors[id] = {
      id,
      label: getFactorShortLabel(factorsMetadata, id, name),
      rebalanced: value,
      original: 0,
      delta: value,
      formatter,
    }
  })

  forEach(originalFactors, ({ id, value, name }) => {
    if (factors[id]) {
      factors[id].original = value
      factors[id].delta = factors[id].rebalanced - value
    } else {
      factors[id] = {
        id,
        label: getFactorShortLabel(factorsMetadata, id, name),
        rebalanced: 0,
        original: value,
        delta: -value,
        formatter,
      }
    }
  })

  return chain(factors)
    .map(item => ({ ...item, favorite: favoriteFactors[item.id] }))
    .orderBy([factor => factor.favorite, factor => Math.abs(factor.original)], ["asc", "desc"])
    .value()
}

export const getSelectedData = createSelector(
  [getDecompositionType, getPortfolioData, getOptimizationData],
  (decompositionType, portfolioData, optimizationData) => {
    const path = compact([
      decompositionType,
      "0",
      decompositionType === "risk" && "attribution",
      "factors",
    ])

    return {
      portfolio: get(portfolioData, path),
      optimization: get(optimizationData, path),
    }
  },
)

const getFormatter = createSelector(
  [getDecompositionType, getSortedContentSets],
  (decompositionType, contentSets) => {
    const contentSet = find(contentSets, ({ escapedId }) => escapedId === decompositionType)
    if (contentSet) {
      return makeCustomFactorFormatter(contentSet.dataType === CONTENT_SET_DATA_TYPES.SENSITIVITY)
    }

    return includes(["risk", "predictedFactorSensitivity", "historicalFactorSensitivity"], decompositionType)
      ? value => numberFormatter(value * 100) : value => numberFormatter(value)
  },
)

export const getSelectedFactors = createSelector(
  [getFactors, getFactorConstraints, getSelectedData, getFormatter],
  (factorsMetadata, factorConstraints, { portfolio, optimization }, formatter) => chain(factorConstraints)
    .map(({ id, exposureWeighting }) => {
      const originalFactor = find(portfolio, ({ id: originalId }) => originalId === id)
      if (!originalFactor) {
        return null
      }
      const original = get(originalFactor, "value", 0)
      const rebalanced = get(find(optimization, ({ id: rebalanced }) => rebalanced === id), "value", 0)

      return {
        id,
        label: getFactorShortLabel(factorsMetadata, id, get(originalFactor, "name")),
        original,
        rebalanced,
        delta: rebalanced - original,
        formatter: exposureWeighting === EXPOSURE_CONSTRAINT_WEIGHTINGS.CURRENCY ? makeCustomFactorFormatter(true) : formatter,
      }
    })
    .compact()
    .value(),
)

export const getCategories = createSelector(
  [getFactorsCategories, getFactors, getFavoriteFactors, getSelectedData, getDecompositionType, getSelectedContentSet, getFormatter],
  (categories, factorsMetadata, allFavoriteFactors, { portfolio, optimization }, decompositionType, selectedContentSet, formatter) => {
    const isRisk = decompositionType === "risk"
    const isContentSet = !!selectedContentSet
    const favoriteFactors = !isContentSet ? allFavoriteFactors : []

    return chain(categories)
      .map(category => {
        if (category.id === "esg" && decompositionType === "risk") { return null }

        const rebalancedFactors = getCategoryFactors(optimization, category.id)
        const originalFactors = getCategoryFactors(portfolio, category.id)
        const factors = combineFactors(factorsMetadata, rebalancedFactors, originalFactors, favoriteFactors, formatter)

        const isTotalable = isRisk || category.singleFactor

        const [rebalanced, original] = isTotalable
          ? [sumBy(rebalancedFactors, factor => factor.value), sumBy(originalFactors, factor => factor.value)]
          : [null, null]
        const delta = isTotalable ? rebalanced - original : null

        return {
          rebalanced,
          original,
          delta,
          factors,
          formatter,
          ...category,
        }
      })
      .filter(category => category && (!category.singleFactor || category.original !== 0 || category.rebalanced !== 0))
      .value()
  },
)
